import { useState, useEffect, type ReactNode } from 'react'
import type { VirtualDesktopRequest } from '../../api/types'
import { useVirtualDesktopCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import { useCalculatorStore } from '../../store/calculatorStore'
import { CurrencyTooltip } from '../shared/CurrencyTooltip'
import { FieldTooltip } from '../shared/FieldTooltip'
import { Disclaimer } from '../shared/Disclaimer'
import { DiscountModifier } from '../shared/DiscountModifier'
import { FeedbackModal } from '../shared/FeedbackModal'
import { FormulaDisclosure } from '../shared/FormulaDisclosure'
import { MobileCostBar } from '../shared/MobileCostBar'
import { ResourcesSection } from '../shared/ResourcesSection'
import { ShareButton } from '../shared/ShareButton'
import { ScenarioPresets } from '../shared/ScenarioPresets'

const VM_SKUS = [
  { value: 'D2s v3', label: 'D2s v3 — 2 vCPU / 8 GB RAM', note: 'Task workers · light applications' },
  { value: 'D4s v3', label: 'D4s v3 — 4 vCPU / 16 GB RAM', note: 'Knowledge workers · recommended default' },
  { value: 'D8s v3', label: 'D8s v3 — 8 vCPU / 32 GB RAM', note: 'Power users / developers' },
  { value: 'B2ms', label: 'B2ms — 2 vCPU / 8 GB RAM', note: 'Burstable · light or intermittent sessions' },
  { value: 'B4ms', label: 'B4ms — 4 vCPU / 16 GB RAM', note: 'Burstable · light office usage' },
] as const

const HOURS_PRESETS = [260, 520, 730] as const

function SectionCard({ icon, title, children }: { icon: string; title: string; children: ReactNode }) {
  return (
    <div
      className="rounded-lg p-6"
      style={{ backgroundColor: 'var(--surface-container-high)', border: '1px solid var(--outline-variant)' }}
    >
      <h2 className="text-[13px] font-bold uppercase tracking-widest mb-5 flex items-center gap-2 text-[#0078d4]">
        <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>
          {icon}
        </span>
        {title}
      </h2>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  )
}

function CompactOption({ selected, label, onClick }: { selected: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex-1 py-2 px-3 rounded text-[11px] font-semibold border transition-all',
        selected ? 'border-[#0078d4] text-[#0078d4]' : '',
      ].join(' ')}
      style={selected
        ? {
            backgroundColor: 'var(--surface-container)',
            borderColor: '#0078d4',
            color: '#0078d4',
            boxShadow: 'inset 0 0 0 1px #0078d4',
          }
        : {
            backgroundColor: 'var(--surface-container-low)',
            borderColor: 'var(--outline-variant)',
            color: 'var(--text-tertiary)',
          }}
    >
      {label}
    </button>
  )
}

export default function VirtualDesktopCalc() {
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const {
    virtualDesktopInputs,
    setVirtualDesktopInputs,
    discount,
    setDiscount,
  } = useCalculatorStore()

  const { data, isLoading, isError } = useVirtualDesktopCalc(virtualDesktopInputs)
  const { trackCalculatorUsed } = useAnalytics()
  useEffect(() => {
    if (data?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'virtual-desktop', result_gbp: data.monthly_estimate, tier: virtualDesktopInputs.sessionHosts.vmSku })
    }
  }, [data?.monthly_estimate])
  const paygTotal = data?.monthly_estimate ?? 0
  const discountedTotal = paygTotal * (1 - discount / 100)
  const visibleTotal = discount > 0 ? discountedTotal : paygTotal
  const totalUsers = data?.total_users ?? (virtualDesktopInputs.users.internalCount + virtualDesktopInputs.users.externalCount)

  const setSessionHosts = (partial: Partial<VirtualDesktopRequest['sessionHosts']>) =>
    setVirtualDesktopInputs({ sessionHosts: partial })
  const setFslogix = (partial: Partial<VirtualDesktopRequest['fslogix']>) =>
    setVirtualDesktopInputs({ fslogix: partial })
  const setUsers = (partial: Partial<VirtualDesktopRequest['users']>) =>
    setVirtualDesktopInputs({ users: partial })
  const setNetworking = (partial: Partial<VirtualDesktopRequest['networking']>) =>
    setVirtualDesktopInputs({ networking: partial })

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 w-full">
      <div className="flex flex-col gap-6">
        <ScenarioPresets presets={[
          {
            label: 'Small team (10 users)',
            description: 'D2s v3 · 2 hosts · business hours · M365 licences included',
            apply: () => { setSessionHosts({ vmSku: 'D2s v3', instanceCount: 2, hoursPerMonth: 176 }); setUsers({ internalCount: 10, externalCount: 0, internalHasEligibleM365: true }); setFslogix({ storageTier: 'standard' }); },
          },
          {
            label: 'Enterprise (100 users)',
            description: 'D4s v3 · 10 hosts · always-on · no M365 assumption · premium FSLogix',
            apply: () => { setSessionHosts({ vmSku: 'D4s v3', instanceCount: 10, hoursPerMonth: 730 }); setUsers({ internalCount: 100, externalCount: 0, internalHasEligibleM365: false }); setFslogix({ storageTier: 'premium' }); },
          },
        ]} />
        <SectionCard icon="computer" title="Session Hosts">
          <div>
            <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
              VM Size
            </label>
            <select
              value={virtualDesktopInputs.sessionHosts.vmSku}
              onChange={(e) => setSessionHosts({ vmSku: e.target.value })}
              className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
              style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
            >
              {VM_SKUS.map((sku) => (
                <option key={sku.value} value={sku.value}>{sku.label}</option>
              ))}
            </select>
            <p className="text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
              {VM_SKUS.find((sku) => sku.value === virtualDesktopInputs.sessionHosts.vmSku)?.note}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Instance Count
              </label>
              <input
                type="number"
                min={1}
                max={500}
                value={virtualDesktopInputs.sessionHosts.instanceCount}
                onChange={(e) => setSessionHosts({ instanceCount: Math.max(1, Number(e.target.value) || 1) })}
                className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
              />
            </div>
            <div>
              <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Hours / Month
              </label>
              <input
                type="number"
                min={0}
                max={744}
                value={virtualDesktopInputs.sessionHosts.hoursPerMonth}
                onChange={(e) => setSessionHosts({ hoursPerMonth: Math.min(744, Math.max(0, Number(e.target.value) || 0)) })}
                className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Hours profile
              </label>
              <span className="text-[12px] font-mono" style={{ color: 'var(--text-primary)' }}>
                {virtualDesktopInputs.sessionHosts.hoursPerMonth} hrs
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={744}
              step={1}
              value={virtualDesktopInputs.sessionHosts.hoursPerMonth}
              onChange={(e) => setSessionHosts({ hoursPerMonth: Number(e.target.value) })}
              className="w-full appearance-none accent-[#0078d4]"
              style={{ backgroundColor: 'var(--slider-track)', height: '4px', borderRadius: '2px' }}
            />
            <div className="flex gap-2">
              {HOURS_PRESETS.map((preset) => (
                <CompactOption
                  key={preset}
                  selected={virtualDesktopInputs.sessionHosts.hoursPerMonth === preset}
                  label={`${preset} hrs`}
                  onClick={() => setSessionHosts({ hoursPerMonth: preset })}
                />
              ))}
            </div>
          </div>

          <div className="bg-[#0078d4]/10 border border-[#0078d4]/30 p-4 flex gap-3 items-start rounded-sm">
            <span className="material-symbols-outlined text-[#0078d4] text-[18px]">info</span>
            <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
              Auto-shutdown during off-hours can cut session host spend by roughly 65% versus always-on. A typical weekday-only deployment often lands nearer 260 hours/month than 730.
            </p>
          </div>

          <div>
            <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
              Hosting Model
            </label>
            <div className="flex gap-2">
              <CompactOption
                selected={virtualDesktopInputs.sessionHosts.hostingModel === 'multi_session'}
                label="Multi-session"
                onClick={() => setSessionHosts({ hostingModel: 'multi_session' })}
              />
              <CompactOption
                selected={virtualDesktopInputs.sessionHosts.hostingModel === 'personal'}
                label="Personal desktop"
                onClick={() => setSessionHosts({ hostingModel: 'personal' })}
              />
            </div>
            <p className="text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
              Multi-session is usually the most cost-effective default. Personal desktops suit isolated power-user or developer workloads.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={virtualDesktopInputs.sessionHosts.azureHybridBenefit}
              onChange={(e) => setSessionHosts({ azureHybridBenefit: e.target.checked })}
              className="mt-0.5 accent-[#0078d4]"
            />
            <div>
              <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>Azure Hybrid Benefit</span>
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                I have eligible Windows Server licences with Software Assurance, so Windows session hosts can be billed at the Linux VM rate.
              </p>
            </div>
          </label>
        </SectionCard>

        <SectionCard icon="folder_open" title="FSLogix Profile Storage">
          <div>
            <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
              Storage Tier <FieldTooltip content="FSLogix profile storage tier. Premium SSD has lower latency; Standard HDD is cheaper but generates more transaction costs." />
            </label>
            <div className="flex gap-2">
              <CompactOption
                selected={virtualDesktopInputs.fslogix.storageTier === 'standard'}
                label="Standard (HDD)"
                onClick={() => setFslogix({ storageTier: 'standard' })}
              />
              <CompactOption
                selected={virtualDesktopInputs.fslogix.storageTier === 'premium'}
                label="Premium (SSD)"
                onClick={() => setFslogix({ storageTier: 'premium' })}
              />
            </div>
          </div>

          {virtualDesktopInputs.fslogix.storageTier === 'standard' && (
            <div className="bg-[#e8b000]/10 border border-[#e8b000]/50 p-4 flex gap-3 items-start rounded-sm">
              <span className="material-symbols-outlined text-[#ffb900] text-[18px]">warning</span>
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                Azure Files Standard charges per transaction. Active AVD estates generate heavy login, logoff, sync, and profile-container IO. Premium often looks more expensive per GB but becomes cheaper in practice once transactions are counted.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Provisioned GB
              </label>
              <input
                type="number"
                min={100}
                max={102400}
                value={virtualDesktopInputs.fslogix.provisionedGB}
                onChange={(e) => setFslogix({ provisionedGB: Math.max(100, Number(e.target.value) || 100) })}
                className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
              />
              <p className="text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                Allow ~10–20 GB per user for office workloads, 30–50 GB for developer or heavier profiles.
              </p>
            </div>

            {virtualDesktopInputs.fslogix.storageTier === 'standard' && (
              <div>
                <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                  Transactions / User / Day
                </label>
                <input
                  type="number"
                  min={0}
                  value={virtualDesktopInputs.fslogix.estimatedTransactionsPerUserPerDay}
                  onChange={(e) => setFslogix({ estimatedTransactionsPerUserPerDay: Math.max(0, Number(e.target.value) || 0) })}
                  className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                  style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
                />
                <p className="text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                  Typical active VDI ranges: 1,000–5,000+ per user per day.
                </p>
              </div>
            )}
          </div>
        </SectionCard>

        <SectionCard icon="group" title="User Licensing">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Internal Users
              </label>
              <input
                type="number"
                min={0}
                value={virtualDesktopInputs.users.internalCount}
                onChange={(e) => setUsers({ internalCount: Math.max(0, Number(e.target.value) || 0) })}
                className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
              />
            </div>
            <div>
              <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                External Users
              </label>
              <input
                type="number"
                min={0}
                value={virtualDesktopInputs.users.externalCount}
                onChange={(e) => setUsers({ externalCount: Math.max(0, Number(e.target.value) || 0) })}
                className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
              />
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={virtualDesktopInputs.users.internalHasEligibleM365}
              onChange={(e) => setUsers({ internalHasEligibleM365: e.target.checked })}
              className="mt-0.5 accent-[#0078d4]"
            />
            <div>
              <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>Internal users have eligible M365 licences</span>
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                M365 E3, E5, F3, and Business Premium typically include AVD access for internal users at no extra per-user fee.
              </p>
            </div>
          </label>

          {virtualDesktopInputs.users.externalCount > 0 && (
            <div>
              <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                External Access Type <FieldTooltip content="External users (non-Azure AD) need a per-user licence. RemoteApp streams individual apps; Full Desktop gives a complete Windows session." />
              </label>
              <div className="flex gap-2">
                <CompactOption
                  selected={virtualDesktopInputs.users.externalAccessType === 'apps_only'}
                  label="Apps only ($5.50)"
                  onClick={() => setUsers({ externalAccessType: 'apps_only' })}
                />
                <CompactOption
                  selected={virtualDesktopInputs.users.externalAccessType === 'apps_and_desktops'}
                  label="Apps + desktops ($10)"
                  onClick={() => setUsers({ externalAccessType: 'apps_and_desktops' })}
                />
              </div>
            </div>
          )}

          <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
            Microsoft publishes AVD access fees in USD. This calculator shows approximate GBP equivalents so you can estimate the full monthly budget alongside UK South infrastructure costs.
          </p>

          <div className="bg-[#0078d4]/10 border border-[#0078d4]/30 p-4 flex gap-3 items-start rounded-sm">
            <span className="material-symbols-outlined text-[#0078d4] text-[18px]">info</span>
            <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
              <strong>Considering Windows 365?</strong> Windows 365 charges a flat monthly fee per user with no separate VM, FSLogix, or licensing components. If your user count and working hours are predictable, it can be simpler to budget than AVD.{` `}
              <a href="https://www.microsoft.com/en-gb/windows-365/all-plans" target="_blank" rel="noopener noreferrer" className="text-[#0078d4] hover:underline">
                Compare Windows 365 pricing →
              </a>
            </p>
          </div>
        </SectionCard>

        <SectionCard icon="swap_horiz" title="Networking">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Outbound Internet GB / Month
              </label>
              <input
                type="number"
                min={0}
                value={virtualDesktopInputs.networking.outboundInternetGB}
                onChange={(e) => setNetworking({ outboundInternetGB: Math.max(0, Number(e.target.value) || 0) })}
                className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
              />
              <p className="text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                RDP / AVD traffic over the public internet. Azure bandwidth pricing includes the first 100 GB per month free.
              </p>
            </div>

            <div>
              <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                NAT Gateway
              </label>
              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setNetworking({ natGatewayEnabled: !virtualDesktopInputs.networking.natGatewayEnabled })}
                  aria-pressed={virtualDesktopInputs.networking.natGatewayEnabled}
                  style={virtualDesktopInputs.networking.natGatewayEnabled
                    ? { backgroundColor: '#0078d4', border: '1px solid #0078d4' }
                    : { backgroundColor: 'var(--outline-variant)', border: '1px solid var(--outline)' }}
                  className="w-12 h-6 rounded-full flex items-center px-1 transition-colors"
                >
                  <span
                    className="w-4 h-4 rounded-full transition-transform bg-white"
                    style={{ transform: virtualDesktopInputs.networking.natGatewayEnabled ? 'translateX(24px)' : 'translateX(0)' }}
                  />
                </button>
                <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                  {virtualDesktopInputs.networking.natGatewayEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>

          {virtualDesktopInputs.networking.natGatewayEnabled && (
            <>
              <div className="bg-[#e8b000]/10 border border-[#e8b000]/50 p-4 flex gap-3 items-start rounded-sm">
                <span className="material-symbols-outlined text-[#ffb900] text-[18px]">warning</span>
                <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                  NAT Gateway has no pause state. It incurs hourly charges until deleted, even if your AVD environment is otherwise idle.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                    NAT Hours / Month
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={744}
                    value={virtualDesktopInputs.networking.natGatewayHoursPerMonth}
                    onChange={(e) => setNetworking({ natGatewayHoursPerMonth: Math.min(744, Math.max(0, Number(e.target.value) || 0)) })}
                    className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                    style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
                  />
                </div>
                <div>
                  <label className="block text-[11px] mb-1 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                    NAT Data Processed GB
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={virtualDesktopInputs.networking.natGatewayDataProcessedGB}
                    onChange={(e) => setNetworking({ natGatewayDataProcessedGB: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                    style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
                  />
                </div>
              </div>
            </>
          )}
        </SectionCard>
      </div>

      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          {isError && (
            <div className="bg-[#a4262c]/10 border border-[#a4262c]/40 p-4 flex gap-3 items-start rounded-sm">
              <span className="material-symbols-outlined text-[#e04040] text-[18px]">error</span>
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                The AVD estimate could not be loaded. Check your inputs and try again.
              </p>
            </div>
          )}

          {data?.fslogix_warning && data.breakdown.fslogix.premiumVsStandardNote && (
            <div className="bg-[#e8b000]/10 border border-[#e8b000]/50 p-4 flex gap-3 items-start rounded-sm">
              <span className="material-symbols-outlined text-[#ffb900] text-[18px]">warning</span>
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                {data.breakdown.fslogix.premiumVsStandardNote}
              </p>
            </div>
          )}

          <div id="avd-result" className="rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--surface-container-high)', borderLeft: '2px solid #0078d4' }}>
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h3 className="font-mono text-[11px] uppercase tracking-widest mb-2" style={{ color: 'var(--text-tertiary)' }}>
                    Total Monthly Cost (PAYG)
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-5xl font-bold text-[#0078d4]">
                      {isLoading ? '...' : `£${visibleTotal.toFixed(2)}`}
                    </span>
                    <CurrencyTooltip />
                  </div>
                  <p className="text-[11px] mt-2" style={{ color: 'var(--text-tertiary)' }}>
                    UK South pricing · session hosts · FSLogix · licensing · networking
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  className="transition-colors hover:text-[#0078d4]"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>

              {discount > 0 && (
                <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                  PAYG £{paygTotal.toFixed(2)} · discount saving £{(paygTotal - discountedTotal).toFixed(2)} / month
                </p>
              )}

              <div className="mt-4 pt-4 flex items-end justify-between" style={{ borderTop: '1px solid var(--outline-variant)' }}>
                <div>
                  <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                    Per User / Month
                  </p>
                  <p className="text-[28px] font-bold" style={{ color: 'var(--text-primary)' }}>
                    £{(totalUsers > 0 ? visibleTotal / totalUsers : visibleTotal).toFixed(2)}
                  </p>
                </div>
                <p className="text-[11px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
                  {totalUsers} users
                </p>
              </div>
            </div>

            {data && (
              <div className="px-6 py-4 flex flex-col gap-2" style={{ backgroundColor: 'var(--surface-container-lowest)' }}>
                <h4 className="text-xs font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--text-tertiary)' }}>
                  <span className="material-symbols-outlined text-sm">receipt_long</span>
                  Breakdown
                </h4>

                <div className="flex justify-between items-center font-mono text-xs gap-4">
                  <span style={{ color: 'var(--text-tertiary)' }}>Session hosts</span>
                  <span className="font-bold" style={{ color: 'var(--text-primary)' }}>£{data.breakdown.sessionHosts.total.toFixed(2)}</span>
                </div>
                {data.breakdown.sessionHosts.hybridBenefitSaving > 0 && (
                  <div className="flex justify-between items-center font-mono text-xs gap-4">
                    <span className="pl-3 text-[#107c10]">Azure Hybrid Benefit saving</span>
                    <span className="font-bold text-[#107c10]">−£{data.breakdown.sessionHosts.hybridBenefitSaving.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between items-center font-mono text-xs gap-4">
                  <span style={{ color: 'var(--text-tertiary)' }}>FSLogix storage</span>
                  <span className="font-bold" style={{ color: 'var(--text-primary)' }}>£{data.breakdown.fslogix.total.toFixed(2)}</span>
                </div>
                {data.breakdown.fslogix.transactionCost > 0 && (
                  <div className="flex justify-between items-center font-mono text-xs gap-4">
                    <span className="pl-3" style={{ color: 'var(--text-tertiary)' }}>Transaction charges</span>
                    <span className="font-bold text-[#e8b000]">£{data.breakdown.fslogix.transactionCost.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between items-center font-mono text-xs gap-4">
                  <span style={{ color: 'var(--text-tertiary)' }}>User licensing</span>
                  <span className="font-bold" style={{ color: 'var(--text-primary)' }}>£{data.breakdown.userLicensing.total.toFixed(2)}</span>
                </div>
                {data.breakdown.userLicensing.internalHasEligibleM365 && data.breakdown.userLicensing.internalCount > 0 && (
                  <div className="flex justify-between items-center font-mono text-xs gap-4">
                    <span className="pl-3 text-[#107c10]">Internal users covered by M365</span>
                    <span className="font-bold text-[#107c10]">£0.00</span>
                  </div>
                )}

                <div className="flex justify-between items-center font-mono text-xs gap-4">
                  <span style={{ color: 'var(--text-tertiary)' }}>Networking</span>
                  <span className="font-bold" style={{ color: 'var(--text-primary)' }}>£{data.breakdown.networking.total.toFixed(2)}</span>
                </div>

                <div className="pt-2 mt-1 flex justify-between items-center font-mono text-sm gap-4" style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <span className="font-bold" style={{ color: 'var(--text-primary)' }}>Total</span>
                  <span className="font-bold" style={{ color: 'var(--text-primary)' }}>£{data.monthly_estimate.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {data && virtualDesktopInputs.fslogix.storageTier === 'standard' && (
            <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
              <h3 className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-tertiary)' }}>
                Standard vs Premium Storage
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className={data.fslogix_warning ? 'border border-[#e8b000]/50 bg-[#e8b000]/10 rounded p-3' : 'rounded p-3'} style={data.fslogix_warning ? {} : { border: '1px solid var(--outline-variant)' }}>
                  <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-tertiary)' }}>Standard</p>
                  <p className="text-[18px] font-bold" style={{ color: 'var(--text-primary)' }}>£{data.breakdown.fslogix.total.toFixed(2)}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Storage + transactions</p>
                </div>
                <div className={data.fslogix_warning ? 'border border-[#107c10]/40 bg-[#107c10]/10 rounded p-3' : 'rounded p-3'} style={data.fslogix_warning ? {} : { border: '1px solid var(--outline-variant)' }}>
                  <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-tertiary)' }}>Premium</p>
                  <p className="text-[18px] font-bold text-[#107c10]">£{data.breakdown.fslogix.premiumEquivalent.toFixed(2)}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Provisioned · no transactions</p>
                </div>
              </div>
            </div>
          )}

          {data && (
            <FormulaDisclosure
              lines={[
                `Session hosts: £${data.breakdown.sessionHosts.effectiveRatePerHour.toFixed(4)}/hr × ${data.breakdown.sessionHosts.instanceCount} × ${data.breakdown.sessionHosts.hoursPerMonth} hrs = £${data.breakdown.sessionHosts.total.toFixed(2)}`,
                `FSLogix: ${data.breakdown.fslogix.provisionedGB} GB on ${data.breakdown.fslogix.storageTier} = £${data.breakdown.fslogix.storageCost.toFixed(2)}${data.breakdown.fslogix.transactionCost > 0 ? ` + £${data.breakdown.fslogix.transactionCost.toFixed(2)} transactions` : ''}`,
                `User licensing: internal £${data.breakdown.userLicensing.internalFeeGBP.toFixed(2)} + external £${data.breakdown.userLicensing.externalFeeGBP.toFixed(2)} = £${data.breakdown.userLicensing.total.toFixed(2)}`,
                `Networking: outbound egress £${data.breakdown.networking.outboundInternetCost.toFixed(2)}${data.breakdown.networking.natGatewayEnabled ? ` + NAT £${(data.breakdown.networking.natGatewayHourlyCost + data.breakdown.networking.natGatewayDataCost).toFixed(2)}` : ''}`,
              ]}
              fetchedAt={data.prices_last_updated}
            />
          )}

          {data && (
            <DiscountModifier
              discount={discount}
              onDiscountChange={setDiscount}
              paygTotal={paygTotal}
            />
          )}

          <div className="p-4 text-[10px] leading-relaxed" style={{ backgroundColor: 'var(--surface-container-lowest)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
            <Disclaimer />
            {data?.disclaimer && <p className="mt-1 italic">{data.disclaimer}</p>}
          </div>

          <ShareButton inputs={virtualDesktopInputs} result={data ?? null} service="virtual-desktop" />

          <ResourcesSection service="virtual-desktop" />
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="virtual-desktop"
          estimateValue={data ? `£${visibleTotal.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}

      <MobileCostBar
        amount={data ? visibleTotal : null}
        label="AVD estimate"
        targetId="avd-result"
        savingsAmount={discount > 0 ? paygTotal - visibleTotal : undefined}
      />
    </div>
  )
}
